import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemButton,
  Container,
  Grid,
  Card,
  CardContent,
  Skeleton,
  IconButton,
  Collapse,
  Tooltip,
} from "@mui/material";
import {
  ExpandMore as ExpandMoreIcon,
  Book as BookIcon,
  CheckCircle as CheckCircleIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
} from "@mui/icons-material";
import { useParams } from "react-router-dom";
import {
  useGetStudentExerciseByIdQuery,
  useGetStudentProgressQuery,
} from "@/store/api/user/exerciseApi";
import { useLazyLoad } from "@/hooks";
import { EXERCISE_PART_TYPES as SECTION_TYPES } from "@/constants/exerciseConstants";
import SectionMainContent from "./SectionMainContent";

const ExerciseDetail = () => {
  const { exerciseId } = useParams();
  const [selectedDay, setSelectedDay] = useState(null);
  const [selectedSection, setSelectedSection] = useState("description");
  const [expandedDay, setExpandedDay] = useState(null);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isSessionActive, setIsSessionActive] = useState(false);
  const {
    data: exerciseResponse,
    isLoading,
    error,
  } = useGetStudentExerciseByIdQuery(exerciseId, { skip: !exerciseId });
  const { data: progressResponse } = useGetStudentProgressQuery(
    exerciseId && selectedDay
      ? { exerciseId, dayId: selectedDay._id }
      : undefined,
    { skip: !exerciseId || !selectedDay }
  );

  const exercise = exerciseResponse?.data;
  const progress = progressResponse?.data;

  // Lazy loading for days
  const {
    displayedItems: displayedDays,
    allItems: sortedDays,
    isLoadingMore,
    hasMore: hasMoreDays,
    loadMore: loadMoreDays,
    loaderRef,
    remainingCount,
    itemsPerLoad,
  } = useLazyLoad(exercise?.days || [], {
    initialLoad: 10,
    itemsPerLoad: 10,
    sortFn: (a, b) => a.dayNumber - b.dayNumber,
    resetDependency: exerciseId,
  });

  useEffect(() => {
    if (exercise?.days?.length > 0 && !selectedDay) {
      setSelectedDay(exercise.days[0]);
    }
  }, [exercise, selectedDay]);

  const handleAccordionChange = (day, isExpanded) => {
    setExpandedDay(isExpanded ? day._id : false);
    setSelectedDay(day);
    setSelectedSection("description");
  };

  const handleSectionClick = (sectionType) => {
    setSelectedSection(sectionType);
  };

  const handleSessionStart = () => {
    setIsSessionActive(true);
    setIsSidebarCollapsed(true);
  };

  const handleSessionEnd = () => {
    setIsSessionActive(false);
    setIsSidebarCollapsed(false);
  };

  const toggleSidebar = () => {
    setIsSidebarCollapsed(!isSidebarCollapsed);
  };

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  if (error || !exercise) {
    return (
      <Box textAlign="center" py={4}>
        <Typography variant="h6" color="error">
          Exercise not found
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background:
          "linear-gradient(135deg, #f0f4f8 0%, #e1e8ed 50%, #e3f2fd 100%)",
        py: 2,
      }}
    >
      <Container maxWidth="xl">
        <Box py={3}>
          {/* Header */}
          <Box mb={3}>
            <Typography 
              variant="h4" 
              fontWeight="bold" 
              gutterBottom
              sx={{
                background: "linear-gradient(135deg, #2196F3 0%, #1976D2 100%)",
                backgroundClip: "text",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              {exercise.title}
            </Typography>
          </Box>

          <Grid container spacing={4}>
          {/* Main Content - Left Side */}
          <Grid item xs={12} md={isSidebarCollapsed ? 11 : 8}>
            <Card
              sx={{
                minHeight: 600,
                borderRadius: 4,
                background: "white",
                backdropFilter: "blur(20px)",
                boxShadow: "0 20px 40px rgba(0,0,0,0.1)",
                border: "1px solid rgba(255,255,255,0.2)",
                overflow: "visible",
              }}
            >
              <CardContent sx={{ p: 2, overflow: "visible" }}>
                <SectionMainContent
                  selectedSection={selectedSection}
                  selectedDay={selectedDay}
                  progress={progress}
                  exerciseId={exerciseId}
                  onSessionStart={handleSessionStart}
                  onSessionEnd={handleSessionEnd}
                  isSidebarCollapsed={isSidebarCollapsed}
                  onToggleSidebar={toggleSidebar}
                />
                </CardContent>
            </Card>
          </Grid>

          {/* Collapsed Sidebar - Slim column with expand button (only on md+ screens) */}
          {isSidebarCollapsed && (
            <Grid item xs={12} md={1} sx={{ display: { xs: 'none', md: 'block' } }}>
              <Card
                sx={{
                  position: "sticky",
                  top: 20,
                  borderRadius: 4,
                  background: "white",
                  backdropFilter: "blur(20px)",
                  boxShadow: "0 20px 40px rgba(0,0,0,0.1)",
                  border: "1px solid rgba(255,255,255,0.2)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  minHeight: 600,
                }}
              >
                <Tooltip title="Show day list" placement="left">
                  <IconButton
                    onClick={toggleSidebar}
                    sx={{
                      bgcolor: "primary.main",
                      color: "white",
                      "&:hover": {
                        bgcolor: "primary.dark",
                      },
                      width: 48,
                      height: 48,
                    }}
                  >
                    <ChevronLeftIcon />
                    <ChevronLeftIcon sx={{ ml: -1.5 }} />
                  </IconButton>
                </Tooltip>
              </Card>
            </Grid>
          )}

          {/* Days Sidebar - Right Side */}
          {!isSidebarCollapsed && (
          <Grid item xs={12} md={4} sx={{ display: { xs: 'block', md: 'block' } }}>
            <Card
              sx={{
                position: "sticky",
                top: 20,
                borderRadius: 4,
                background: "white",
                backdropFilter: "blur(20px)",
                boxShadow: "0 20px 40px rgba(0,0,0,0.1)",
                border: "1px solid rgba(255,255,255,0.2)",
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <BookIcon color="primary" />
                    <Typography variant="h6" fontWeight="bold">
                      Learning Days
                    </Typography>
                  </Box>
                  <Tooltip title="Collapse sidebar">
                    <IconButton size="small" onClick={toggleSidebar}>
                      <ChevronRightIcon />
                    </IconButton>
                  </Tooltip>
                </Box>

                <Box>
                  {displayedDays.map((day) => (
                    <Accordion
                      key={day._id}
                      expanded={expandedDay === day._id}
                      onChange={(event, isExpanded) =>
                        handleAccordionChange(day, isExpanded)
                      }
                      sx={{
                        mb: 2,
                        "&:before": { display: "none" },
                        borderRadius: 3,
                        background: selectedDay?._id === day._id 
                          ? "rgba(33, 150, 243, 0.1)" 
                          : "rgba(255,255,255,0.9)",
                        backdropFilter: "blur(10px)",
                        border: "1px solid",
                        borderColor:
                          selectedDay?._id === day._id ? "#2196F3" : "rgba(255,255,255,0.3)",
                        boxShadow: selectedDay?._id === day._id 
                          ? "0 8px 20px rgba(33, 150, 243, 0.2)" 
                          : "0 4px 12px rgba(0,0,0,0.1)",
                        transition: "all 0.3s ease",
                        "&:hover": {
                          transform: "translateY(-2px)",
                          boxShadow: "0 8px 20px rgba(0,0,0,0.15)",
                        },
                        "&:first-of-type": {
                          borderRadius: 3,
                        },
                        "&:last-of-type": {
                          borderRadius: 3,
                        },
                      }}
                    >
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        sx={{
                          borderRadius: selectedDay?._id === day._id ? "12px 12px 0 0" : 3,
                          background:
                            selectedDay?._id === day._id
                              ? "linear-gradient(135deg, #2196F3 0%, #1976D2 100%)"
                              : "transparent",
                          color:
                            selectedDay?._id === day._id ? "white" : "#2c3e50",
                          "&:hover": {
                            background:
                              selectedDay?._id === day._id
                                ? "linear-gradient(135deg, #1976D2 0%, #1565C0 100%)"
                                : "rgba(33, 150, 243, 0.05)",
                          },
                          transition: "all 0.3s ease",
                        }}
                      >
                        <Box
                          display="flex"
                          alignItems="center"
                          gap={2}
                          width="100%"
                        >
                          <Typography variant="subtitle1" fontWeight="bold">
                            Day {day.dayNumber}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ flexGrow: 1 }}
                          >
                            {day.title}
                          </Typography>
                          {progress?.dayProgress?.[day._id] && (
                            <CheckCircleIcon color="success" fontSize="small" />
                          )}
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails sx={{ 
                        p: 2, 
                        background: "rgba(255,255,255,0.5)",
                        backdropFilter: "blur(5px)",
                        borderRadius: "0 0 12px 12px",
                      }}>
                        <List dense>
                          {(() => {
                            const visibleSections =
                              day.dayNumber === 0
                                ? ["description"]
                                : Object.keys(SECTION_TYPES);
                            return visibleSections.map((sectionKey) => {
                              const sectionData = SECTION_TYPES[sectionKey];
                              const isCompleted =
                                progress?.partProgress?.[sectionData.route];
                              const IconComponent = sectionData.icon;
                              const isSelected =
                                selectedSection === sectionKey &&
                                selectedDay?._id === day._id;
                              return (
                                <ListItem key={sectionKey} disablePadding>
                                  <ListItemButton
                                    onClick={() => handleSectionClick(sectionKey)}
                                    sx={{
                                      py: 0.5,
                                      px: 1,
                                      borderRadius: 1,
                                      mb: 0.5,
                                      bgcolor: isSelected ? `#d4f1ff` : "transparent",
                                      "&:hover": {
                                        bgcolor: isSelected
                                          ? `${sectionData.color}.100`
                                          : "grey.100",
                                      },
                                    }}
                                  >
                                    <ListItemIcon sx={{ minWidth: 32 }}>
                                      <IconComponent
                                        fontSize="small"
                                        color={isCompleted ? "success" : sectionData.color}
                                      />
                                    </ListItemIcon>
                                    <ListItemText
                                      primary={sectionData.name}
                                      primaryTypographyProps={{
                                        variant: "body2",
                                        fontWeight: isSelected ? "bold" : "normal",
                                        color: isSelected
                                          ? `${sectionData.color}.main`
                                          : "text.primary",
                                      }}
                                    />
                                    {isCompleted && (
                                      <CheckCircleIcon
                                        color="success"
                                        fontSize="small"
                                        sx={{ ml: 1 }}
                                      />
                                    )}
                                  </ListItemButton>
                                </ListItem>
                              );
                            });
                          })()}
                        </List>
                      </AccordionDetails>
                    </Accordion>
                  ))}

                  {/* Loading skeleton for lazy loading */}
                  {isLoadingMore && (
                    <Box sx={{ mb: 1 }}>
                      {[...Array(Math.min(itemsPerLoad, remainingCount))].map((_, index) => (
                        <Card key={`skeleton-${index}`} sx={{ mb: 2, p: 2, borderRadius: 3 }}>
                          <Box display="flex" alignItems="center" gap={2}>
                            <Skeleton variant="text" width="20%" height={30} />
                            <Skeleton variant="text" width="50%" height={30} />
                            <Skeleton variant="circular" width={24} height={24} />
                          </Box>
                        </Card>
                      ))}
                    </Box>
                  )}

                  {/* Intersection observer target */}
                  {hasMoreDays && (
                    <Box
                      ref={loaderRef}
                      sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        py: 2,
                        minHeight: 50,
                      }}
                    >
                      {!isLoadingMore && (
                        <Typography variant="caption" color="text.secondary">
                          Scroll to load more ({remainingCount} remaining)
                        </Typography>
                      )}
                    </Box>
                  )}

                  {/* Load more button (fallback) */}
                  {hasMoreDays && !isLoadingMore && (
                    <Box display="flex" justifyContent="center" mt={1}>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={loadMoreDays}
                        disabled={isLoadingMore}
                        sx={{
                          borderRadius: 2,
                          textTransform: 'none',
                        }}
                      >
                        Load More Days ({remainingCount})
                      </Button>
                    </Box>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>
          )}
          </Grid>
        </Box>
      </Container>
    </Box>
  );
};

export default ExerciseDetail;
